const TOKEN_KEY = 'plate_token';
const USER_KEY = 'plate_user';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export interface StoredUser {
  id: number;
  email: string;
  household_id?: number;
  household_role?: string;
  permissions?: Record<string, boolean>;
  display_name?: string;
  onboarding_completed?: boolean;
}

export function getUser(): StoredUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function setUser(user: Record<string, unknown>): void {
  const stored: StoredUser = {
    id: user.id as number,
    email: user.email as string,
  };
  if (user.household_id !== undefined) stored.household_id = user.household_id as number;
  if (user.household_role !== undefined) stored.household_role = user.household_role as string;
  if (user.permissions !== undefined) stored.permissions = user.permissions as Record<string, boolean>;
  if (user.display_name !== undefined) stored.display_name = user.display_name as string;
  if (user.onboarding_completed !== undefined) stored.onboarding_completed = user.onboarding_completed as boolean;
  localStorage.setItem(USER_KEY, JSON.stringify(stored));
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`/api${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    clearAuth();
    window.location.replace('/login');
    return new Promise<never>(() => {}); // never resolves – page will redirect
  }

  if (res.status === 402) {
    const body = await res.json().catch(() => ({ message: 'Nicht genug Credits' }));
    // Auto-show credit error dialog
    window.dispatchEvent(new CustomEvent('credit-error', { detail: body }));
    // Also refresh credit badge
    window.dispatchEvent(new Event('credits-updated'));
    throw {
      status: 402,
      credit_error: body.credit_error,
      message: body.message || 'Nicht genug Credits',
      household_balance: body.household_balance,
    };
  }

  if (!res.ok) {
    const errorBody = await res.text().catch(() => '');
    throw { status: res.status, message: errorBody || `HTTP ${res.status}` };
  }

  if (res.status === 204) {
    return undefined as T;
  }

  // Refresh credit badge after AI actions
  if (method === 'POST' && path.startsWith('/ai/')) {
    window.dispatchEvent(new Event('credits-updated'));
  }

  return res.json();
}

export function get<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

export function post<T>(path: string, data?: unknown): Promise<T> {
  return request<T>('POST', path, data);
}

export function patch<T>(path: string, data?: unknown): Promise<T> {
  return request<T>('PATCH', path, data);
}

export function put<T>(path: string, data?: unknown): Promise<T> {
  return request<T>('PUT', path, data);
}

export function del<T = void>(path: string): Promise<T> {
  return request<T>('DELETE', path);
}
